import { createFetchAdapter, createHqApi } from '@hq/api-sdk';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface HqRuntimeConfig {
  apiBase: string;
  resBase: string;
  appTitle: string;
  uploadMaxMb: number;
  enableDebug: boolean;
}

export interface HqProfile {
  userId: string;
  /** 监管主体：卫健局或医共体牵头院（data_scope=ALL / SUPERVISED_ORG）。 */
  orgId: string;
  deptId: string;
  name: string;
  level: string;
  /** 租户名称（`sys_tenant.tenant_name`，如「杭州市富阳区」），侧栏品牌位展示。 */
  tenantName?: string | null;
  /** 后端下发的权限码；undefined 表示旧会话/开发态发号，不做前端菜单裁剪。 */
  perms?: string[];
  /** 个人账户：机构/科室名称、工号、头像、脱敏手机、邮箱。 */
  orgName?: string | null;
  deptName?: string | null;
  userCode?: string | null;
  avatarUrl?: string | null;
  mobile?: string | null;
  email?: string | null;
}

declare global {
  interface Window {
    __HQ_CONFIG__?: Partial<HqRuntimeConfig>;
  }
}

const fallback: HqRuntimeConfig = {
  apiBase: '/api',
  resBase: '/res',
  appTitle: '后勤物资智慧监管平台',
  uploadMaxMb: 50,
  enableDebug: false
};

/** 本机 dev 强制同源，走 Vite 反代（5007→5001/5002）；生产绝对地址由 config.js 提供。 */
const devOverride: Partial<HqRuntimeConfig> = import.meta.env.DEV ? { apiBase: '/api', resBase: '/res' } : {};

const runtime: HqRuntimeConfig = { ...fallback, ...(window.__HQ_CONFIG__ ?? {}), ...devOverride };

export const config: HqRuntimeConfig = runtime;

export const appTitle = config.appTitle;

interface AuthState {
  token: string | null;
  profile: HqProfile | null;
  setSession: (token: string, profile: HqProfile) => void;
  /** 会话补全（如旧会话缺少 tenantName 时由后端 /auth/me 回填）。 */
  patchProfile: (patch: Partial<HqProfile>) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      profile: null,
      setSession: (token, profile) => set({ token, profile }),
      patchProfile: (patch) =>
        set((state) => ({ profile: state.profile ? { ...state.profile, ...patch } : null })),
      clear: () => set({ token: null, profile: null })
    }),
    {
      name: 'hq-gov-auth',
      storage: createJSONStorage(() => sessionStorage)
    }
  )
);

const getToken = (): string | null => useAuthStore.getState().token;

export const hq = createHqApi({
  adapter: createFetchAdapter(),
  baseUrl: config.apiBase,
  resBaseUrl: config.resBase,
  resolveToken: getToken,
  onUnauthorized: () => useAuthStore.getState().clear()
});
